
import React from 'react';
import { format } from 'date-fns';
import { Calendar } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { STATE_REPORT_CONFIGS } from '@/utils/reportUtils';
import { useIsMobile } from '@/hooks/use-mobile';
import { ResponsiveTable } from '@/components/ui/responsive-table';

interface UpcomingDeadlinesProps {
  onSelectDeadline: (year: number, quarter: number, state: string) => void;
}

interface Deadline {
  state: string;
  year: number;
  quarter?: number;
  month?: number;
  dueDate: Date;
  status: 'upcoming' | 'due' | 'overdue' | 'completed';
}

export function UpcomingDeadlines({ onSelectDeadline }: UpcomingDeadlinesProps) {
  const isMobile = useIsMobile();
  
  // Calculate upcoming report deadlines
  const getUpcomingDeadlines = (): Deadline[] => {
    const deadlines: Deadline[] = [];
    const today = new Date();
    
    // Get available states from the configuration
    const availableStates = Object.keys(STATE_REPORT_CONFIGS);
    
    for (const stateCode of availableStates) {
      const config = STATE_REPORT_CONFIGS[stateCode];
      if (!config || !config.submissionFrequency) continue;
      
      if (config.submissionFrequency === 'quarterly') {
        // Calculate current quarter
        const currentQuarter = Math.floor((today.getMonth() / 3)) + 1;
        const currentYear = today.getFullYear();
        
        // Due date is X days after quarter end
        const quarterEndMonth = currentQuarter * 3;
        const quarterEndDate = new Date(currentYear, quarterEndMonth, 0);
        const dueDate = new Date(quarterEndDate);
        dueDate.setDate(dueDate.getDate() + (config.submissionDeadlineDays || 30));
        
        // Determine status
        let status: 'upcoming' | 'due' | 'overdue' | 'completed' = 'upcoming';
        if (today > dueDate) {
          status = 'overdue';
        } else if (today >= quarterEndDate) {
          status = 'due';
        }
        
        deadlines.push({
          state: stateCode,
          year: currentYear,
          quarter: currentQuarter,
          dueDate,
          status,
        });
      }
    }
    
    return deadlines;
  };
  
  const upcomingDeadlines = getUpcomingDeadlines();
  
  if (upcomingDeadlines.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Calendar className="mr-2 h-5 w-5" />
          Upcoming Report Deadlines
        </CardTitle>
        <CardDescription>
          Reports that need to be submitted soon to comply with state regulations
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveTable compact={isMobile}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>State</TableHead>
                <TableHead>Period</TableHead>
                {!isMobile && <TableHead>Due Date</TableHead>}
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {upcomingDeadlines.map((deadline, index) => (
                <TableRow key={index}>
                  <TableCell>{deadline.state}</TableCell>
                  <TableCell>
                    {deadline.quarter ? `Q${deadline.quarter} ${deadline.year}` : 
                    deadline.month ? `${format(new Date(deadline.year, deadline.month-1, 1), 'MMMM yyyy')}` : 
                    deadline.year}
                  </TableCell>
                  {!isMobile && <TableCell>{format(deadline.dueDate, 'MMM d, yyyy')}</TableCell>}
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                      ${deadline.status === 'overdue' ? 'bg-red-100 text-red-800' :
                        deadline.status === 'due' ? 'bg-yellow-100 text-yellow-800' :
                        deadline.status === 'completed' ? 'bg-green-100 text-green-800' : 
                        'bg-blue-100 text-blue-800'}`}>
                      {deadline.status.charAt(0).toUpperCase() + deadline.status.slice(1)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {deadline.status !== 'completed' && (
                      <Button 
                        variant="outline" 
                        size={isMobile ? "sm" : "default"}
                        onClick={() => {
                          if (deadline.quarter) {
                            onSelectDeadline(deadline.year, deadline.quarter, deadline.state);
                          }
                        }}
                      >
                        Generate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ResponsiveTable>
      </CardContent>
    </Card>
  );
}
